import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import { EditActions, editActions } from "../../actions/editActions";
import IoConfig from "./IoConfig/IoConfigView";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import StandardBoards from "./StandardBoards/StandardBoards";
import DevelopmentPackage from "./DevelopmentPackage/DevelopmentPackage";
import {
  isEditActiveSelector,
  editedObjectIdentifierSelector,
  editedObjectIdSelector,
  parentObjectIdSelector
} from "../../reducers/editReducer";
import IoConfigToString from "./IoConfigToString/IoConfigToString";
import { hasRole } from "../../utils/utils";
import LicenseData from "./LicenseData/LicenseData";
import LicenseUploadPortal from "../../components/licenseUpload/LicenseUploadPortal";
import { createEmptyCommonEnumeration } from "../../utils/pdApiUtils";
import { pdApiActions, PdApiActions } from "../../actions/pdApiActions";
import ActionMenu from "../common/ActionMenu/ActionMenu";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import CommonEnumerations from "./CommonEnumerations/CommonEnumerations";
import { mainActions } from "../../actions/mainActions";

type StateProps = {
  pdApi: State.PdApi | undefined;
  activeUser: Api.User | undefined;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: string;
  parentObjectId: string;
  editedObjectType: string;
  isEditActive: boolean;
  objectIdentifier: string | number | undefined;
  filterOptions: State.FilterOptions;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
  editActions: typeof editActions;
  mainActions: typeof mainActions;
};

type OwnProps = {
  subMenuRoute: State.ToolsSubmenu;
};

// Props of the component
export type ToolsProps = StateProps & DispatchProps & OwnProps;

type ToolsState = {
  isModalOpen: boolean;
};

export class Tools extends React.Component<ToolsProps, ToolsState> {
  state = {
    isModalOpen: false
  };

  private createEmptyAndSetObjectType = () => {
    this.props.editActions.setEditedType("enumeration");
    return createEmptyCommonEnumeration(this.props.pdApi);
  };

  /**
   * To add new sub page to this parent, add it here
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subPages: any = {
    "Common Enumerations": {
      itemName: "enumeration",
      PdApiField: "enumerations",
      dataView: CommonEnumerations,
      createEmpty: this.createEmptyAndSetObjectType
    },
    "IO configuration": {
      dataView: IoConfig
    },
    "Standard Boards": {
      dataView: StandardBoards
    },
    "Development Package": {
      dataView: DevelopmentPackage
    },
    "Open binary config file": {
      dataView: IoConfigToString
    },
    "License Data": {
      dataView: LicenseData
    },
    "License Upload": {
      dataView: LicenseUploadPortal
    }
  };

  render(): JSX.Element {
    const { subMenuRoute, activeUser } = this.props;
    const submenuLinks = (
      <>
        <NavigationLink
          handleOnClick={this.props.editActions.cancelEditing}
          name="IO configuration"
          to={"/tools/io-configuration/"}
        />
        <NavigationLink
          handleOnClick={this.props.editActions.cancelEditing}
          name="Common Enumerations"
          to={"/tools/common-enumerations/"}
        />
        <NavigationLink
          handleOnClick={this.props.editActions.cancelEditing}
          name="Development Package"
          to={"/tools/development-package/"}
        />
        {hasRole(activeUser, "license-generation") && (
          <NavigationLink
            handleOnClick={this.props.editActions.cancelEditing}
            name="License Data"
            to={"/tools/license-data/"}
          />
        )}
        {hasRole(activeUser, "license-upload") && (
          <NavigationLink
            handleOnClick={this.props.editActions.cancelEditing}
            name="License Upload"
            to={"/tools/license-upload/"}
          />
        )}
      </>
    );

    const actionMenu = this.getActionMenu(subMenuRoute);
    const dataView = this.getDataView(subMenuRoute);
    const modal = this.buildModal();

    return (
      <>
        <ContentLayout submenu={submenuLinks} actionMenu={actionMenu} dataView={dataView} />
        {modal}
      </>
    );
  }

  private getDataView = (subMenuRoute: State.ToolsSubmenu): JSX.Element => {
    if (subMenuRoute in this.subPages) {
      return React.createElement(this.subPages[subMenuRoute].dataView, {
        ...this.props,
        toggleModal: this.toggleModal
      });
    }
    return <>Error</>;
  };

  private getActionMenu = (subMenuRoute: State.ToolsSubmenu): JSX.Element | null => {
    if (
      subMenuRoute === "IO configuration" ||
      subMenuRoute === "Standard Boards" ||
      subMenuRoute === "Select Configuration" ||
      subMenuRoute === "Development Package" ||
      subMenuRoute === "Open binary config file" ||
      subMenuRoute === "License Data" ||
      subMenuRoute === "License Upload" ||
      this.subPages[subMenuRoute].PdApiField === null
    ) {
      return null;
    }

    return (
      <ActionMenu
        {...this.props}
        itemName={this.subPages[subMenuRoute].itemName}
        selectedColumnsName={subMenuRoute}
        dataFieldName={this.subPages[subMenuRoute].PdApiField}
        setExtraProperties={this.subPages[subMenuRoute].setExtraProperties}
        createEmpty={this.subPages[subMenuRoute].createEmpty}
        showExport={subMenuRoute !== "Common Enumerations"}
        showTableSettings={subMenuRoute !== "Common Enumerations"}
      />
    );
  };

  private buildModal = () => {
    const content = (
      <span>
        Are you sure you want to delete this {this.props.editedObjectType}?
        <br />
        {this.props.objectIdentifier}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={this.state.isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() => {
          this.props.editActions.delete(
            (this.props.editedObjectType === "enumeration"
              ? "enumerations"
              : "enumerationItems") as Api.OptionsGrud
          );
        }}
        onCancel={this.toggleModal}
      />
    );
  };

  /*
   * Toggles the modal based on previous state.
   */
  private toggleModal = (): void => {
    this.setState(prevState => ({ ...prevState, isModalOpen: !prevState.isModalOpen }));
  };
}
const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  parentObjectId: parentObjectIdSelector(state.editView),
  editedObjectType: state.editView.editedObjectType,
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),
  filterOptions: state.filterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions | EditActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Tools);

